import React, { FunctionComponent } from 'react'

import { useTranslation } from 'react-i18next'

import styled from 'styled-components'

import { Text, baseColors } from '@fairhq/common'

import { BellCurve } from 'components/BellCurve'
import { DifferenceTag } from 'components/DifferenceTag'
import { DemGroupCount, SurveyWindow } from 'store/surveyWindow/types'

import { CharlieBenchmark } from './CharlieBenchmark'
import { ComparisonSquareBar } from './ComparisonSquareBar'

const FlexWrapper = styled.div`
  display: flex;
  width: 100%;
`

const Container = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 20% auto;
  column-gap: 20px;
`

const BarsWrapper = styled(FlexWrapper)`
  gap: 4px;
  flex-direction: column;
`

const BarWrapper = styled.div`
  display: grid;
  grid-template-columns: 100px 250px 80px auto;
  height: 28px;
  align-items: center;
`

const PeerComparisonCurve = styled.div`
  display: grid;
  justify-items: end;
  align-items: center;
`

const NameWrapper = styled.div`
  height: 100%;
  border-right: 1px solid ${baseColors.borderBackground};
  display: flex;
  align-items: center;
  justify-content: end;
  padding-right: 8px;
`

const NoWrapText = styled(Text)`
  white-space: nowrap;
`

const Approval = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  svg {
    flex-shrink: 0;
  }
`

interface KeyIndustryDemographicsProps {
  demCode: string
  latestSurveyWindow: SurveyWindow
  previousSurveyWindow?: SurveyWindow
  latestDem?: DemGroupCount
  previousDem?: DemGroupCount
  benchmark: number
}

export const KeyDemographic: FunctionComponent<
  KeyIndustryDemographicsProps
> = ({
  demCode,
  latestSurveyWindow,
  previousSurveyWindow,
  latestDem,
  previousDem,
  benchmark,
}) => {
  const { t } = useTranslation()

  return (
    <Container>
      <Text element="p" type="body" size="sm" fontWeight={500}>
        {t(`${demCode}.parentGroup`)}
      </Text>
      <FlexWrapper>
        <BarsWrapper>
          <Text element="p" type="body" size="2xs" fontWeight={400}>
            {t(`${demCode}.helper`)}
          </Text>
          {previousSurveyWindow && (
            <BarWrapper>
              <NameWrapper>
                <NoWrapText element="p" type="body" size="2xs" fontWeight={400}>
                  {previousSurveyWindow.name}
                </NoWrapText>
              </NameWrapper>
              <ComparisonSquareBar
                percentage={previousDem ? +previousDem.rate * 100 : 0}
                color={baseColors.contentMuted}
                borderColor={baseColors.borderDefault}
              />
            </BarWrapper>
          )}

          <BarWrapper>
            <NameWrapper>
              <NoWrapText element="p" type="body" size="2xs" fontWeight={400}>
                {latestSurveyWindow.name}
              </NoWrapText>
            </NameWrapper>
            <ComparisonSquareBar
              percentage={latestDem ? +latestDem.rate * 100 : 0}
              color={baseColors.boldActionPrimary}
              borderColor={baseColors.borderPrimary}
            />
            {latestDem && previousDem && (
              <DifferenceTag
                value={+latestDem.rate - +previousDem.rate}
                displayValue={`${Math.round(
                  (+latestDem.rate - +previousDem.rate) * 100
                )}%`}
              />
            )}

            <Approval>
              <CharlieBenchmark benchmark={benchmark} />
            </Approval>
          </BarWrapper>
        </BarsWrapper>

        {latestDem?.percentile && (
          <PeerComparisonCurve>
            <BellCurve value={+latestDem.percentile} />
          </PeerComparisonCurve>
        )}
      </FlexWrapper>
    </Container>
  )
}
